import { ref, computed, watch } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import type { Address } from '@models/address.ts';
import { Requests } from '@tools/requests';
import { eventBus } from '../event-bus';
import { flashBagError } from '../flashbag';
import { URL_XHR_ADDRESSES } from '../routes/routes';
import { useFavoriteAddressesStore } from './useFavoriteAddressesStore.ts';

export const useAddressFilterStore = defineStore('addressFilter', () => {
  interface xhrAddressResult {
    '0': Address;
  }

  const { addressesIds: favoriteAddressesIds } = storeToRefs(useFavoriteAddressesStore());

  const availableAddresses = ref<Address[]>([]);

  const availableAddressesIds = computed<number[]>(() => availableAddresses.value.map((address) => address.id));

  const urlAddressesIds = computed<number[]>({
    get: () => {
      const urlParams = new URLSearchParams(window.location.search);
      const urlIds = urlParams.get('addresses');
      if (!urlIds) {
        return [];
      }
      return urlIds.split(',').filter((id) => /^\d+$/.test(id)).map((id) => parseInt(id, 10));
    },
    set: (ids: number[]) => {
      const url = new URL(window.location.href);
      if (ids.length > 0) {
        url.searchParams.set('addresses', ids.join(','));
      } else {
        url.searchParams.delete('addresses');
      }
      window.history.replaceState({}, '', url.toString().replace(/%2C/g, ','));
    },
  });

  const initialSelectedAddressesIds: number[] = urlAddressesIds.value.length ? urlAddressesIds.value : favoriteAddressesIds.value;
  const selectedAddressesIds = ref<number[]>(initialSelectedAddressesIds);
  urlAddressesIds.value = selectedAddressesIds.value;

  const selectedAddresses = computed<Address[]>({
    get: () => availableAddresses.value.filter((address) => selectedAddressesIds.value.includes(address.id)),
    set: (addresses: Address[]) => {
      selectedAddressesIds.value = addresses.map((address) => address.id);
    },
  });

  const selectedAddressesIdsString = computed<string>(() => selectedAddressesIds.value.join(','));

  watch(selectedAddressesIds, (ids) => {
    urlAddressesIds.value = ids;
  });

  const init = async () => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', '1');
    queryParams.append('page_items', '1000');
    try {
      const { data } = await Requests.get(`${URL_XHR_ADDRESSES}?${queryParams.toString()}`);
      availableAddresses.value = data.addresses.map((result: xhrAddressResult) => result['0']);
    } catch (error) {
      flashBagError();
    }
  };

  const select = (address: Address): void => {
    const addressId = address.id;
    if (!availableAddressesIds.value.includes(addressId) || selectedAddressesIds.value.includes(addressId)) {
      return;
    }
    selectedAddressesIds.value.push(addressId);
  };

  const unselect = (address: Address): void => {
    const addressId = address.id;
    if (!selectedAddressesIds.value.includes(addressId)) {
      return;
    }
    selectedAddressesIds.value = selectedAddressesIds.value.filter((id) => id !== addressId);
  };

  eventBus.addEventListener('reset-inbox-filters', () => {
    selectedAddressesIds.value = [];
  });

  return {
    selectedAddressesIds,
    selectedAddressesIdsString,
    selectedAddresses,
    availableAddresses,
    init,
    select,
    unselect,
  };
});
